import { useMemo } from 'react';
import { useI18n } from '../../i18n';
import { countryName } from '../../i18n/labels';
import { EUROPEAN_COUNTRY_CODES, flagEmoji, type CountryCode } from '../../lib/countries';

interface Props {
  value: string;
  onChange: (code: CountryCode) => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
}

/**
 * Country picker for ISO 3166-1 alpha-2 European nations. Sorted by the
 * current locale's country name, not by code. Returns an opinionated value
 * (one of EUROPEAN_COUNTRY_CODES) — caller doesn't have to re-validate.
 */
export default function CountryPicker({ value, onChange, required, disabled, className = '', id, ...aria }: Props) {
  const { t } = useI18n();
  const sorted = useMemo(
    () => [...EUROPEAN_COUNTRY_CODES].sort((a, b) => countryName(t, a).localeCompare(countryName(t, b))),
    [t],
  );
  return (
    <select
      id={id}
      aria-describedby={aria['aria-describedby']}
      aria-invalid={aria['aria-invalid']}
      className={`input ${className}`}
      value={value}
      onChange={e => onChange(e.target.value as CountryCode)}
      required={required}
      disabled={disabled}
    >
      {sorted.map(code => (
        <option key={code} value={code}>
          {flagEmoji(code)} {countryName(t, code)}
        </option>
      ))}
    </select>
  );
}
